import { isProtectedTable } from 'utils/recentlyExitedTables';

/**
 * Tables to exit because their tournament is back to Announced and the player
 * has no seat there.
 *
 * A recurring MTT (daily, weekly, …) resets to Announced ('A') a few minutes
 * after it finishes: the engine deletes its tables, opens a new one for the
 * next run, and the player-games list drops everyone. A table still open on
 * the client belongs to a tournament that hasn't opened registration, and
 * nothing else closes it (every prompt keys on R / P / F / C), so it stayed in
 * the nav. The player can have no seat at an Announced tournament, so it is
 * exited.
 *
 * The status is the table's own (`game.status` is the live tournament status)
 * while it reads a running tournament (R / P). Otherwise the lobby's
 * tournament list decides: the deleted table may send nothing more, leaving
 * its last frame's 'F', while the lobby already lists the next run as 'A'.
 *
 * Kept:
 *   - a table the player is seated at, or that their tournament details still
 *     assign them to (registered or playing there),
 *   - the balancing destination (protected; mid-move its payload can lag),
 *   - a table mid final-table reshuffle (isPostBalancing = 1).
 */
export function findAnnouncedTournamentTablesToExit(
  tables: Record<string, any> | undefined | null,
  playerTournamentDetails: any[] | undefined | null,
  lobbyTournaments: any[] | undefined | null = []
): { gameID: string; gameType: string }[] {
  const assigned = new Set(
    (Array.isArray(playerTournamentDetails) ? playerTournamentDetails : [])
      .filter((d) => d?.status === 'P' || d?.status === 'R')
      .map((d) => String(d?.gameID))
  );
  const lobbyStatus = new Map<number, string>();
  (Array.isArray(lobbyTournaments) ? lobbyTournaments : []).forEach((t) => {
    if (Number(t?.tournamentId) > 0) lobbyStatus.set(Number(t.tournamentId), t.status);
  });

  const tournamentStatusOf = (game: any): string | undefined =>
    game?.status === 'R' || game?.status === 'P'
      ? game.status
      : lobbyStatus.get(Number(game?.tournamentId)) ?? game?.status;

  return Object.entries(tables || {})
    .filter(([gid, table]) => {
      const game = table?.game;
      if (game?.tabletype !== 'm' && game?.tabletype !== 't') return false;
      if (tournamentStatusOf(game) !== 'A') return false;
      const isSeated =
        table?.currentPlayer?.isSeated === true || Number(table?.currentPlayer?.seatId ?? 0) > 0;
      if (isSeated || assigned.has(gid)) return false;
      if (isProtectedTable(gid) || Number(game?.isPostBalancing) === 1) return false;
      return true;
    })
    .map(([gid, table]) => ({ gameID: gid, gameType: table?.game?.game || 'texas' }));
}
